<template>
  <section v-if="loading || tescoPortalStubsEnabled">
    <page-sub-header title="Support" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`support-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else-if="tescoPortalStubsEnabled">
      <!-- New vs Resolved Tickets Comparison with Conversations -->
      <sme-card v-if="newTicketsDataset.values.length" class="mb-4" v-pdf-chart-group>
        <p class="mb-4 mt-2 text-center">
          {{ timeGroupTitle }}, {{ toInteger(lastNewTickets) }} new tickets were created from
          {{ toInteger(lastConversations) }} conversations. {{ toInteger(lastResolvedTickets) }} tickets were resolved.
        </p>
        <pdf-chart title="Tickets and conversations over time" subtitle="Count">
          <line-chart
            :labels="timeGroupDataset"
            :datasets="[
              timeGroupedConversationsDataset,
              timeGroupedNewTicketsDataset,
              timeGroupedResolvedTicketsDataset,
            ]"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: 'Count' } } },
              plugins: { legend: { display: true, position: 'top' } },
            }"
            fill-gradient
            class="mb-4"
          />
        </pdf-chart>
        <b-row>
          <b-col cols="12" lg="4">
            <app-stat
              :stat="lastConversations"
              :previous-stat="prevConversations"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Conversations`"
              class="mb-1"
              background-color="core-3-lighten-80"
              color="default"
              pdf-kpi
            />
          </b-col>
          <b-col cols="12" lg="4">
            <app-stat
              :stat="lastNewTickets"
              :previous-stat="prevNewTickets"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} New Tickets`"
              class="mb-1"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
          </b-col>
          <b-col cols="12" lg="4">
            <app-stat
              :stat="lastResolvedTickets"
              :previous-stat="prevResolvedTickets"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Resolved Tickets`"
              class="mb-1"
              background-color="core-2-lighten-90"
              color="default"
              pdf-kpi
            />
          </b-col>
        </b-row>
      </sme-card>

      <!-- CSAT Metrics Row -->
      <b-row class="mb-4">
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="csatScoreDataset.calculated.mean || 0"
              :formatter="value => `${toNumber(value)}%`"
              description="Average CSAT Score"
              :class="[csatScoreDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="CSAT score over time">
              <line-chart
                v-if="csatScoreDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="csatScoreDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="csatRequestRateDataset.calculated.mean || 0"
              :formatter="value => `${toNumber(value)}%`"
              description="CSAT survey request rate"
              :class="[csatRequestRateDataset.values.length && 'mb-3']"
              background-color="core-2-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="CSAT survey request rate">
              <line-chart
                v-if="csatRequestRateDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="csatRequestRateDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="csatResponseRateDataset.calculated.mean || 0"
              :formatter="value => `${toNumber(value)}%`"
              description="CSAT survey response rate"
              :class="[csatResponseRateDataset.values.length && 'mb-3']"
              background-color="core-3-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="CSAT survey response rate">
              <line-chart
                v-if="csatResponseRateDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="csatResponseRateDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>

      <!-- Median Time Metrics Row -->
      <b-row class="mb-4">
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="medianResponseTimeDataset.calculated.mean || 0"
              :formatter="value => `${Math.round(value)}h`"
              description="Median time to close"
              :class="[medianResponseTimeDataset.values.length && 'mb-3']"
              background-color="core-5-lighten-90"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Median time to close">
              <line-chart
                v-if="medianResponseTimeDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="medianResponseTimeDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="medianTicketProgressDataset.calculated.mean || 0"
              :formatter="value => `${Math.round(value * 60)} min`"
              description="Median agent response time"
              :class="[medianTicketProgressDataset.values.length && 'mb-3']"
              background-color="core-1-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Median agent response time">
              <line-chart
                v-if="medianTicketProgressDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="medianTicketProgressDataset"
                :chart-options="
                  merge({}, CHART_PRESET_OPTIONS.MINIMAL, {
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: context =>
                            `${context.dataset.label || 'Response Time'}: ${Math.round(context.parsed.y * 60)} min`,
                        },
                      },
                    },
                  })
                "
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
        <b-col cols="4" lg="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden" v-pdf-chart-group>
            <app-stat
              :stat="medianCustomerWaitDataset.calculated.mean || 0"
              :formatter="value => `${Math.round(value)}h`"
              description="Median user response time"
              :class="[medianCustomerWaitDataset.values.length && 'mb-3']"
              background-color="core-4-lighten-80"
              color="default"
              small
              pdf-kpi
            />
            <pdf-chart title="Median user response time">
              <line-chart
                v-if="medianCustomerWaitDataset.values.length"
                :labels="timeGroupDataset"
                :datasets="medianCustomerWaitDataset"
                :chart-options="merge({}, CHART_PRESET_OPTIONS.MINIMAL)"
                :display-points="false"
                class="mt-auto"
                fill-gradient
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>

      <!-- Ticket Categories -->
      <b-row class="mb-4">
        <b-col cols="12" lg="12" class="mb-4">
          <sme-card v-pdf-chart-group>
            <h6 class="mb-3">Resolved Tickets by Category (%, l30d)</h6>
            <pdf-chart title="Resolved tickets by category" subtitle="Percentage (%)">
              <bar-chart
                :series-x="ticketCategoriesSeriesX"
                :series-y="ticketCategoriesSeriesY"
                :chart-options="{
                  aspectRatio: getInitialAspectRatio(3, 1.5),
                  plugins: { legend: { display: false } },
                  scales: {
                    y: { ticks: { precision: 0 }, title: { text: 'Percentage (%)' } },
                    x: { title: { text: 'Category' } },
                  },
                }"
              />
            </pdf-chart>
          </sme-card>
        </b-col>
      </b-row>
    </template>
  </section>
</template>

<script setup>
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import PdfChart from '@/components/PdfChart.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import BarChart from '@/components/charts/BarChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { getInitialAspectRatio, TIME_SERIES_VALUES } from '@/utils/chart';
import { toInteger, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';

const { tescoPortalStubsEnabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const newTicketsDataset = ref(null);
const resolvedTicketsDataset = ref(null);
const conversationsDataset = ref(null);
const csatScoreDataset = ref(null);
const csatRequestRateDataset = ref(null);
const csatResponseRateDataset = ref(null);
const medianResponseTimeDataset = ref(null);
const medianTicketProgressDataset = ref(null);
const medianCustomerWaitDataset = ref(null);
const ticketCategoriesDataset = ref([]);

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
} = useTimeGroupDataset(timeDataset);

const timeGroupedNewTicketsDataset = computed(() => datasetToTimeGroupedDataset(newTicketsDataset));
const timeGroupedResolvedTicketsDataset = computed(() => datasetToTimeGroupedDataset(resolvedTicketsDataset));
const timeGroupedConversationsDataset = computed(() => datasetToTimeGroupedDataset(conversationsDataset));
const lastNewTickets = computed(() => timeGroupedNewTicketsDataset.value?.calculated.last);
const prevNewTickets = computed(() => timeGroupedNewTicketsDataset.value?.calculated.prev);
const lastResolvedTickets = computed(() => timeGroupedResolvedTicketsDataset.value?.calculated.last);
const prevResolvedTickets = computed(() => timeGroupedResolvedTicketsDataset.value?.calculated.prev);
const lastConversations = computed(() => timeGroupedConversationsDataset.value?.calculated.last);
const prevConversations = computed(() => timeGroupedConversationsDataset.value?.calculated.prev);

// Ticket categories data for Bar Chart
const ticketCategoriesSeriesX = computed(() => ({
  values: ticketCategoryLabels.map((label, index) => ({
    name: label,
    color: ticketCategoriesDataset.value.length
      ? ticketCategoriesDataset.value[0].backgroundColor[index]
      : PaletteColors['core-1'],
  })),
}));

const ticketCategoriesSeriesY = computed(() => ({
  name: 'Resolved Tickets (%)',
  values: ticketCategoriesDataset.value.length ? ticketCategoriesDataset.value[0].values : [],
}));

// Ticket category labels
const ticketCategoryLabels = [
  'Savings Setup & Deposits',
  'Savings Withdrawals & Access',
  'Pay & Earnings Access',
  'Balance & Transaction Queries',
  'App Navigation & How It Works',
  'Account Setup & Login Issues',
  'Employer & Payroll Integration',
  'Cards & Payments',
  'Compliance & Security',
  'Fees & Limits',
];

// Simple seeded random function for consistent data
const seededRandom = seed => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const generateSupportMetrics = () => {
  if (!tescoPortalStubsEnabled.value) {
    return;
  }

  // Scale factor for weekly vs monthly
  const timeSeriesMultiplier = timeSeries.value === 'w2w' ? 20 / 4.3 : 20;

  // Generate fake time dataset (last 12 periods)
  const periods = timeSeries.value === 'w2w' ? 12 : 12;
  const timeValues = [];
  const currentTime = Math.floor(Date.now() / 1000);
  const periodLength = timeSeries.value === 'w2w' ? 7 * 24 * 60 * 60 : 30 * 24 * 60 * 60; // seconds

  for (let i = periods - 1; i >= 0; i--) {
    timeValues.push(currentTime - i * periodLength);
  }

  timeDataset.value = {
    values: timeValues,
    calculated: {
      last: timeValues[timeValues.length - 1],
      last_complete: timeValues[timeValues.length - 1],
    },
  };

  // Generate new tickets
  const newTicketsValues = timeValues.map((_, index) => {
    // Use completely different seeds for weekly vs monthly to avoid correlation
    const seedOffset = timeSeries.value === 'w2w' ? 5000 : 1000;
    const seed = seedOffset + index;
    const baseTickets = 30 + (seededRandom(seed) - 0.5) * 30; // 15-45
    return Math.max(5, Math.round(baseTickets * timeSeriesMultiplier));
  });

  newTicketsDataset.value = {
    name: 'New Tickets',
    values: newTicketsValues,
    lineColor: PaletteColors['core-1'],
  };

  // Generate resolved tickets (slightly lower than new, to show backlog growth)
  const resolvedTicketsValues = timeValues.map((_, index) => {
    // Use completely different seeds for weekly vs monthly to avoid correlation
    const seedOffset = timeSeries.value === 'w2w' ? 6000 : 2000;
    const seed = seedOffset + index;
    const newTicketsCount = newTicketsValues[index];
    const resolutionRate = 0.85 + (seededRandom(seed) - 0.5) * 0.3; // 70-100%
    return Math.max(0, Math.round(newTicketsCount * resolutionRate));
  });

  resolvedTicketsDataset.value = {
    name: 'Resolved Tickets',
    values: resolvedTicketsValues,
    lineColor: PaletteColors['core-2'],
  };

  // Generate conversations (higher than tickets, as some tickets have multiple conversations)
  const conversationsValues = timeValues.map((_, index) => {
    // Use completely different seeds for weekly vs monthly to avoid correlation
    const seedOffset = timeSeries.value === 'w2w' ? 7000 : 3000;
    const seed = seedOffset + index;
    const newTicketsCount = newTicketsValues[index];
    // Adjust conversation ratio based on time series to hit target monthly conversations (~2000)
    const baseRatio = timeSeries.value === 'w2w' ? 2.5 : 3.3;
    const conversationRatio = baseRatio + (seededRandom(seed) - 0.5) * 1; // ±0.5 variation
    return Math.round(newTicketsCount * conversationRatio);
  });

  conversationsDataset.value = {
    name: 'Conversations',
    values: conversationsValues,
    lineColor: PaletteColors['core-3'],
    borderDash: [5, 5], // Dashed line
    fill: false, // No fill area
    calculated: {
      mean: conversationsValues.reduce((a, b) => a + b, 0) / conversationsValues.length,
    },
  };

  // Generate CSAT Score (75-95%) - different curve shapes for weekly vs monthly
  const csatScoreValues = timeValues.map((_, index) => {
    let baseScore;
    if (timeSeries.value === 'w2w') {
      // Weekly: More volatile, jagged pattern with sharper fluctuations
      const seed = 8000 + index; // Different seed range for weekly
      const volatility = (seededRandom(seed) - 0.5) * 15; // Higher volatility
      const trend = Math.sin(index * 0.8) * 3; // Faster oscillation
      baseScore = 86 + volatility + trend;
    } else {
      // Monthly: Smoother, more gradual changes with seasonal trends
      const seed = 4200 + index;
      const seasonality = Math.sin(index * 0.3) * 8; // Slower, broader waves
      const gradualTrend = (seededRandom(seed) - 0.5) * 6; // Lower volatility
      baseScore = 84 + seasonality + gradualTrend;
    }
    return Math.max(70, Math.min(100, Math.round(baseScore * 10) / 10));
  });

  csatScoreDataset.value = {
    name: 'CSAT Score (%)',
    values: csatScoreValues,
    lineColor: PaletteColors['core-4'],
    calculated: {
      mean: csatScoreValues.reduce((a, b) => a + b, 0) / csatScoreValues.length,
    },
  };

  // Generate CSAT Request Rate (60-85%) - different curve shapes for weekly vs monthly
  const csatRequestRateValues = timeValues.map((_, index) => {
    let baseRate;
    if (timeSeries.value === 'w2w') {
      // Weekly: Erratic spikes and dips (campaign-driven behavior)
      const seed1 = 9000 + index; // Different seed range for weekly
      const seed2 = 9050 + index;
      const spikes = seededRandom(seed1) > 0.7 ? 15 : 0; // Random spikes
      const baseVariation = (seededRandom(seed2) - 0.5) * 12;
      baseRate = 70 + spikes + baseVariation;
    } else {
      // Monthly: More predictable, campaign-cycle pattern
      const seed = 5100 + index;
      const campaignCycle = Math.cos(index * 0.4) * 10; // Campaign rhythm
      const smoothVariation = (seededRandom(seed) - 0.5) * 8;
      baseRate = 75 + campaignCycle + smoothVariation;
    }
    return Math.max(55, Math.min(90, Math.round(baseRate * 10) / 10));
  });

  csatRequestRateDataset.value = {
    name: 'CSAT Request Rate (%)',
    values: csatRequestRateValues,
    lineColor: PaletteColors['core-2'],
    calculated: {
      mean: csatRequestRateValues.reduce((a, b) => a + b, 0) / csatRequestRateValues.length,
    },
  };

  // Generate CSAT Response Rate (25-45%) - different curve shapes for weekly vs monthly
  const csatResponseRateValues = timeValues.map((_, index) => {
    let baseRate;
    if (timeSeries.value === 'w2w') {
      // Weekly: Sharp drops and recoveries (immediate response patterns)
      const seed1 = 10000 + index; // Different seed range for weekly
      const seed2 = 10025 + index;
      const dropPattern = seededRandom(seed1) < 0.3 ? -8 : 2; // Occasional sharp drops
      const recovery = index > 0 && seededRandom(seed2) > 0.6 ? 6 : 0;
      const noise = (seededRandom(seed1 + 100) - 0.5) * 10;
      baseRate = 37 + dropPattern + recovery + noise;
    } else {
      // Monthly: Gradual decline/improvement trends
      const seed = 6100 + index;
      const trendDirection = index < 6 ? -1 : 1; // Decline then improve
      const gradualTrend = trendDirection * index * 0.8;
      const seasonalEffect = Math.sin(index * 0.2) * 4;
      const smoothNoise = (seededRandom(seed) - 0.5) * 6;
      baseRate = 33 + gradualTrend + seasonalEffect + smoothNoise;
    }
    return Math.max(20, Math.min(50, Math.round(baseRate * 10) / 10));
  });

  csatResponseRateDataset.value = {
    name: 'CSAT Response Rate (%)',
    values: csatResponseRateValues,
    lineColor: PaletteColors['core-3'],
    calculated: {
      mean: csatResponseRateValues.reduce((a, b) => a + b, 0) / csatResponseRateValues.length,
    },
  };

  // Generate component times first (these will add up to response time)
  // Generate Median Response Time - around 15 minutes (0.25 hours)
  const progressTimeValues = timeValues.map((_, index) => {
    let baseTime;
    if (timeSeries.value === 'w2w') {
      // Weekly: Irregular bursts and quiet periods (workload surges)
      const seed1 = 11000 + index; // Different seed range for weekly
      const seed2 = 11030 + index;
      const workloadSurge = seededRandom(seed1) > 0.6 ? 0.1 : 0; // Surge periods (6 min)
      const dailyVariation = Math.sin(index * 1.2) * 0.05; // Day-of-week patterns (3 min)
      const randomNoise = (seededRandom(seed2) - 0.5) * 0.1; // Random noise (6 min)
      baseTime = 0.25 + workloadSurge + dailyVariation + randomNoise;
    } else {
      // Monthly: Staff capacity cycles and process improvements
      const seed = 8100 + index;
      const capacityCycle = Math.cos(index * 0.35) * 0.05; // Staff capacity cycles (3 min)
      const processImprovement = -index * 0.005; // Gradual improvement over time
      const smoothVariation = (seededRandom(seed) - 0.5) * 0.05; // Smooth variation (3 min)
      baseTime = 0.25 + capacityCycle + processImprovement + smoothVariation;
    }
    return Math.max(0.08, Math.round(baseTime * 100) / 100); // Min 5 minutes
  });

  // Generate Median User Response Time - around 5 hours
  const customerWaitValues = timeValues.map((_, index) => {
    let baseTime;
    if (timeSeries.value === 'w2w') {
      // Weekly: Communication gaps and weekend delays
      const seed1 = 12000 + index; // Different seed range for weekly
      const seed2 = 12040 + index;
      const weekendEffect = index % 3 === 0 ? 2 : 0; // Weekend delays (2h)
      const communicationGap = seededRandom(seed1) < 0.25 ? 3 : 0; // Communication gaps (3h)
      const urgencyFactor = (seededRandom(seed2) - 0.5) * 2; // Urgency factor (±1h)
      baseTime = 5 + weekendEffect + communicationGap + urgencyFactor;
    } else {
      // Monthly: User engagement cycles and training effects
      const seed = 9100 + index;
      const engagementCycle = Math.sin(index * 0.25) * 1.5; // User engagement patterns (±1.5h)
      const trainingEffect = index > 3 ? -0.5 : 0; // Training improves response (-0.5h)
      const businessCycle = Math.cos(index * 0.15) * 1; // Business cycle (±1h)
      const variation = (seededRandom(seed) - 0.5) * 1; // Variation (±0.5h)
      baseTime = 5 + engagementCycle + trainingEffect + businessCycle + variation;
    }
    return Math.max(1, Math.round(baseTime * 10) / 10);
  });

  // Calculate median time to close as initial response + user response + resolution time (to reach ~24h total)
  const responseTimeValues = progressTimeValues.map((progressTime, index) => {
    // Target total: ~24h, with ~0.25h response time + ~5h user response + ~18.75h resolution work
    const baseResolutionTime = 18.75; // Hours for ticket resolution work
    // Use different seeds for weekly vs monthly to avoid correlation
    const seedOffset = timeSeries.value === 'w2w' ? 13000 : 7000;
    const resolutionVariation = (seededRandom(seedOffset + index) - 0.5) * 6; // ±3h variation
    const totalTime = progressTime + customerWaitValues[index] + baseResolutionTime + resolutionVariation;
    return Math.round(totalTime * 10) / 10;
  });

  medianResponseTimeDataset.value = {
    name: 'Response Time (hours)',
    values: responseTimeValues,
    lineColor: PaletteColors['core-5'],
    calculated: {
      mean: responseTimeValues.reduce((a, b) => a + b, 0) / responseTimeValues.length,
    },
  };

  medianTicketProgressDataset.value = {
    name: 'Response Time',
    values: progressTimeValues,
    lineColor: PaletteColors['core-1'],
    calculated: {
      mean: progressTimeValues.reduce((a, b) => a + b, 0) / progressTimeValues.length,
    },
  };

  medianCustomerWaitDataset.value = {
    name: 'User Response Time (hours)',
    values: customerWaitValues,
    lineColor: PaletteColors['core-4'],
    calculated: {
      mean: customerWaitValues.reduce((a, b) => a + b, 0) / customerWaitValues.length,
    },
  };

  // Generate ticket categories data as percentages
  const categoryDistribution = [15, 12, 18, 14, 11, 8, 7, 6, 5, 4]; // 10 categories totaling 100% (percentages)

  ticketCategoriesDataset.value = [
    {
      name: 'Resolved Tickets (%)',
      values: categoryDistribution,
      backgroundColor: [
        PaletteColors['core-1'],
        PaletteColors['core-2'],
        PaletteColors['core-3'],
        PaletteColors['core-4'],
        PaletteColors['core-5'],
        PaletteColors['success'],
        PaletteColors['warning'],
        PaletteColors['danger'],
        PaletteColors['brand-primary'],
        PaletteColors['default-lighten-50'],
      ],
    },
  ];
};

watch([divisionId, timeSeries], () => {
  generateSupportMetrics();
});

onBeforeMount(() => {
  loading.value = true;
  generateSupportMetrics();
  loading.value = false;
});

const isDataReady = computed(() => {
  if (!tescoPortalStubsEnabled.value) {
    return false;
  }
  return (
    timeGroupDataset.value?.values &&
    newTicketsDataset.value?.values &&
    resolvedTicketsDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    newTickets: newTicketsDataset.value.values[index],
    resolvedTickets: resolvedTicketsDataset.value.values[index],
    conversations: conversationsDataset.value.values[index],
    csatScore: csatScoreDataset.value.values[index],
    csatRequestRate: csatRequestRateDataset.value.values[index],
    csatResponseRate: csatResponseRateDataset.value.values[index],
    medianTimeToClose: medianResponseTimeDataset.value.values[index],
    responseTime: Math.round(medianTicketProgressDataset.value.values[index] * 60), // Convert to minutes
    userResponseTime: medianCustomerWaitDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    'New Tickets',
    'Resolved Tickets',
    'Conversations',
    'CSAT Score (%)',
    'CSAT Request Rate (%)',
    'CSAT Response Rate (%)',
    'Median Time to Close (h)',
    'Response Time (min)',
    'User Response Time (h)',
  ];
});

const exportFileRows = item => [
  item.time,
  item.newTickets,
  item.resolvedTickets,
  item.conversations,
  item.csatScore,
  item.csatRequestRate,
  item.csatResponseRate,
  item.medianTimeToClose,
  item.responseTime,
  item.userResponseTime,
];
</script>
